import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ModalForm } from '@/components/common/Modal'
import {
  TextInput, SelectInput, PhotoInput, CheckboxInput, FormRow
} from '@/components/common/FormField'
import { departmentService, studentService } from '@/services/api'
import toast from 'react-hot-toast'

const STATUS_OPTIONS = [
  { value: 'active', label: 'Активен' },
  { value: 'expelled', label: 'Отчислен' },
  { value: 'academic_leave', label: 'Академический отпуск' },
  { value: 'graduated', label: 'Выпускник' },
]

const INITIAL = {
  full_name: '', student_id: '', face_id: '', phone: '',
  email: '', department: '', direction: '', group: '',
  enrollment_date: new Date().toISOString().slice(0, 10),
  status: 'active', notes: '',
}

export default function StudentModal({ open, onClose, student = null }) {
  const qc = useQueryClient()
  const [form, setForm] = useState(INITIAL)
  const [photo, setPhoto] = useState(null)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const isEdit = !!student

  const { data: detail } = useQuery({
    queryKey: ['student-detail', student?.id],
    queryFn: () => studentService.get(student.id).then((r) => r.data),
    enabled: open && !!student,
    staleTime: 30000,
  })

  const { data: depts } = useQuery({
    queryKey: ['departments-list'],
    queryFn: () => departmentService.list({ page_size: 500 }).then((r) => r.data.results || r.data),
    enabled: open,
  })

  const { data: directions } = useQuery({
    queryKey: ['directions-for', form.department],
    queryFn: () => departmentService.directions({ department: form.department, page_size: 500 }).then((r) => r.data.results || r.data),
    enabled: !!form.department,
  })

  const { data: groups } = useQuery({
    queryKey: ['groups-for', form.direction],
    queryFn: () => departmentService.classes({ direction: form.direction, page_size: 500 }).then((r) => r.data.results || r.data),
    enabled: !!form.direction,
  })

  useEffect(() => {
    if (open) {
      if (!student) setForm(INITIAL)
      setPhoto(null)
      setErrors({})
    }
  }, [open, student])

  useEffect(() => {
    if (open && student && detail) {
      setForm({
        full_name: detail.full_name || '',
        student_id: detail.student_id || '',
        face_id: detail.face_id || '',
        phone: detail.phone || '',
        email: detail.email || '',
        department: detail.department || '',
        direction: detail.direction || '',
        group: detail.group || '',
        enrollment_date: detail.enrollment_date || INITIAL.enrollment_date,
        status: detail.status || 'active',
        notes: detail.notes || '',
      })
    }
  }, [detail])

  const set = (key) => (e) => {
    const val = e.target.value
    setForm((f) => {
      const next = { ...f, [key]: val }
      if (key === 'department') { next.direction = ''; next.group = '' }
      if (key === 'direction') { next.group = '' }
      return next
    })
    if (errors[key]) setErrors((er) => ({ ...er, [key]: '' }))
  }

  const validate = () => {
    const e = {}
    if (!form.full_name.trim()) e.full_name = 'Обязательное поле'
    if (!form.student_id.trim()) e.student_id = 'Обязательное поле'
    if (!form.face_id.trim()) e.face_id = 'Обязательное поле'
    if (!form.department) e.department = 'Выберите кафедру'
    if (!form.direction) e.direction = 'Выберите направление'
    if (!form.group) e.group = 'Выберите группу'
    if (!form.enrollment_date) e.enrollment_date = 'Обязательное поле'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      const fd = new FormData()
      Object.entries(form).forEach(([k, v]) => v !== '' && v !== null && fd.append(k, v))
      if (photo) fd.append('photo', photo)

      if (isEdit) {
        await studentService.update(student.id, fd)
        toast.success('Студент обновлён')
      } else {
        await studentService.create(fd)
        toast.success('Студент добавлен')
      }
      qc.invalidateQueries(['students'])
      qc.invalidateQueries(['student-detail', student?.id])
      onClose()
    } catch (err) {
      const data = err.response?.data
      if (data) {
        const fieldErrors = {}
        Object.entries(data).forEach(([k, v]) => {
          fieldErrors[k] = Array.isArray(v) ? v[0] : v
        })
        setErrors(fieldErrors)
      } else {
        toast.error('Ошибка сохранения')
      }
    } finally {
      setLoading(false)
    }
  }

  const deptOpts = (depts || []).map((d) => ({ value: d.id, label: d.name }))
  const dirOpts = (directions || []).map((d) => ({ value: d.id, label: d.name }))
  const groupOpts = (groups || []).map((g) => ({ value: g.id, label: g.name }))

  return (
    <ModalForm
      open={open}
      onClose={onClose}
      title={isEdit ? 'Редактировать студента' : 'Добавить студента'}
      onSubmit={handleSubmit}
      loading={loading}
      size="lg"
    >
      <PhotoInput
        label="Фото"
        value={isEdit && (detail?.photo || student?.photo) ? (detail?.photo || student?.photo) : null}
        onChange={(e) => setPhoto(e.target.files[0])}
      />

      <TextInput
        label="ФИО" required
        value={form.full_name} onChange={set('full_name')}
        error={errors.full_name} placeholder="Иванов Иван Иванович"
      />

      <FormRow>
        <TextInput
          label="Номер студента" required
          value={form.student_id} onChange={set('student_id')}
          error={errors.student_id} placeholder="ST-2024-001"
        />
        <TextInput
          label="Face ID (ID в терминале)" required
          value={form.face_id} onChange={set('face_id')}
          error={errors.face_id} placeholder="12345"
          hint="Уникальный ID для Hikvision"
        />
      </FormRow>

      <FormRow>
        <TextInput
          label="Телефон"
          value={form.phone} onChange={set('phone')}
          placeholder="+998 90 000 00 00" type="tel"
        />
        <TextInput
          label="Email"
          value={form.email} onChange={set('email')}
          placeholder="student@univer.uz" type="email"
        />
      </FormRow>

      <SelectInput
        label="Кафедра" required
        value={form.department} onChange={set('department')}
        options={deptOpts} placeholder="Выберите кафедру"
        error={errors.department}
      />
      <SelectInput
        label="Направление" required
        value={form.direction} onChange={set('direction')}
        options={dirOpts} placeholder={form.department ? 'Выберите направление' : 'Сначала выберите кафедру'}
        error={errors.direction}
        disabled={!form.department}
      />
      <SelectInput
        label="Группа" required
        value={form.group} onChange={set('group')}
        options={groupOpts} placeholder={form.direction ? 'Выберите группу' : 'Сначала выберите направление'}
        error={errors.group}
        disabled={!form.direction}
      />

      <FormRow>
        <TextInput
          label="Дата поступления" required
          type="date" value={form.enrollment_date}
          onChange={set('enrollment_date')}
          error={errors.enrollment_date}
        />
        <SelectInput
          label="Статус"
          value={form.status} onChange={set('status')}
          options={STATUS_OPTIONS}
        />
      </FormRow>
    </ModalForm>
  )
}
